import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewContainerRef,
    Injector,
    ComponentRef,
} from '@angular/core';
import { TaskTypes, TaskActionTypes } from '../../constants.new';
import { InstanceRepository } from '../../Entity/Instance/instance.repository';
import { UserInstanceService } from '../../User/userInstance.service';
import { TaskService } from '../task.service.upgrade';
import { WeissmanDateTimeFormatPipe } from '../../UI-Lib/Pipes';
import { CommonModule } from '@angular/common';
import { UserTeamPickerComponent } from '../../Common/User-Pickers/user-team-picker.component';
import { lastValueFrom } from 'rxjs';

import * as _ from 'lodash';
import moment from 'moment';
import * as d3 from 'd3-v7';
import { TimerService } from '../../UI-Lib/Utilities';

interface Vector2 {
    x: number;
    y: number;
}

interface ContextMenuItem {
    label: string;
    action?: () => void;
    children?: ContextMenuItem[];
}

@Component({
    selector: 'task-series',
    templateUrl: './taskSeries.component.html',
    styleUrls: ['./taskSeries.component.scss'],
    imports: [CommonModule, WeissmanDateTimeFormatPipe],
})
export class TaskSeriesComponent implements OnInit, AfterViewInit, OnChanges {
    constructor(
        private readonly _taskService: TaskService,
        private readonly _instanceRepository: InstanceRepository,
        private readonly _userInstanceService: UserInstanceService,
        private readonly _cdRef: ChangeDetectorRef,
        private readonly _elementRef: ElementRef,
        private readonly _vcr: ViewContainerRef,
        private readonly _injector: Injector,
        private readonly _timer: TimerService,
    ) {}

    @Input() taskGroup: any;
    @Input() chevronWidth: number;
    @Input() padding: number;
    @Input() popoverLeftOrRight: string;
    @Input() waitingOnServer: boolean;
    @Input() allowAddYearPrompt: boolean = false;

    @Output() updateData: EventEmitter<any[]> = new EventEmitter();
    @Output() entityTaskUpdated: EventEmitter<void> = new EventEmitter();
    @Output() taskOperationStarted: EventEmitter<Promise<void>> = new EventEmitter();
    @Output() addYearPromptRequested: EventEmitter<Core.AddYearPromptInfo> = new EventEmitter();

    heights = {
        sm: 50, // one line
        md: 75, // two lines
        lg: 100, // three lines
        xl: 125, // four lines
    };

    chevronDivot = this.heights.lg * 0.1;
    svg: d3.Selection<SVGSVGElement, unknown, null, undefined>;
    node;
    enteringNode;
    completeNode;
    currentNode;
    completeText;
    completeChevron;
    completeCheck;
    completeDate;
    verticalOffset = 0;
    completeChevronHeight = this.heights.lg;
    instanceIdUserFilter = null;
    editingTask;
    hoveredTask;
    popoverPosition: Vector2 = {
        x: 0,
        y: 0,
    };
    contextMenu: ContextMenuItem[];

    private _pickerCompRef: ComponentRef<UserTeamPickerComponent> | null = null;
    private _pickerHostDiv: HTMLElement | null = null;
    private _pickerSubscriptions: any[] = [];

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        if (!this.contextMenu || !this.contextMenu.length) {
            return;
        }

        const target = event.target as Node;
        const popEl = (this._elementRef.nativeElement as HTMLElement).querySelector(
            '.task-popover.context-menu',
        ) as HTMLElement | null;
        if (popEl?.contains(target)) {
            return;
        }

        this.hideContextMenus();
    }

    ngOnInit(): void {
        this.initSvg();
        this.defineBoxShadow();
        this.initCompleteChevron();
    }

    ngAfterViewInit(): void {
        //Need to delay to let the modal container render on the screen to get proper dimensions
        this._timer.setTimeout(() => this.updateTaskGroup(), 1);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.taskGroup && changes.taskGroup.currentValue) {
            this.updateTaskGroup();
        }
    }

    initSvg(): void {
        this.svg = d3
            .select(this._elementRef.nativeElement)
            .append('svg')
            .attr('width', this.chevronWidth + this.padding * 2)
            .attr('height', 0);
    }

    defineBoxShadow(): void {
        const defs = this.svg.append('defs');

        // create filter with id #drop-shadow
        // height=130% so that the shadow is not clipped
        const filter = defs.append('filter').attr('id', 'drop-shadow').attr('height', '130%');

        // SourceAlpha refers to opacity of graphic that this filter will be applied to
        // convolve that with a Gaussian with standard deviation 3 and store result
        // in blur
        filter.append('feGaussianBlur').attr('in', 'SourceAlpha').attr('stdDeviation', 2).attr('result', 'blur');

        // translate output of Gaussian blur to the right and downwards with 2px
        // store result in offsetBlur
        filter.append('feOffset').attr('in', 'blur').attr('dx', 2).attr('dy', 2).attr('result', 'offsetBlur');

        // overlay original SourceGraphic over translated blurred opacity by using
        // feMerge filter. Order of specifying inputs is important!
        const feMerge = filter.append('feMerge');

        feMerge.append('feMergeNode').attr('in', 'offsetBlur');
        feMerge.append('feMergeNode').attr('in', 'SourceGraphic');
    }

    initCompleteChevron(): void {
        const completeChevronPath = d3.line()([
            [0, 0],
            [this.chevronWidth / 2, this.chevronDivot],
            [this.chevronWidth, 0],
            [this.chevronWidth, this.completeChevronHeight - this.chevronDivot],
            [0, this.completeChevronHeight - this.chevronDivot],
            [0, 0],
        ]);

        this.completeNode = this.svg
            .append('g')
            .attr('oncontextmenu', 'return false')
            .attr('fill-opacity', 1e-6)
            .attr('stroke-opacity', 1e-6);

        this.completeChevron = this.completeNode
            .append('path')
            .attr('d', completeChevronPath)
            .attr('stroke-width', 1)
            .attr('fill', '#eee')
            .style('filter', 'url(#drop-shadow)');

        this.completeText = this.completeNode
            .append('text')
            .attr('y', this.completeChevronHeight / 2)
            .attr('dy', '0.35em')
            .attr('x', this.chevronWidth / 2)
            .attr('text-anchor', 'middle')
            .attr('class', 'chevron-text')
            .text('Tasks Complete!');

        this.completeCheck = this.completeNode
            .append('text')
            .attr('y', this.completeChevronHeight / 2)
            .attr('x', 10 + this.padding)
            .attr('dy', '0.35em')
            .style('font', 'var(--fa-font-regular)')
            .style('font-weight', '900')
            .style('font-size', '36')
            .attr('class', 'task-checked');

        this.completeDate = this.completeNode
            .append('text')
            .attr('y', this.completeChevronHeight * 0.42 + 20)
            .attr('dy', '0.35em')
            .attr('x', this.chevronWidth / 2)
            .attr('text-anchor', 'middle')
            .attr('class', 'chevron-text');
    }

    updateTaskGroup(): void {
        if (!this.svg) {
            return;
        }

        this.verticalOffset = (this.taskGroup.maxHeight - this.taskGroup.height) / this.taskGroup.tasks.length;

        this.svg.transition().attr('height', this.taskGroup.maxHeight);

        this.node = this.svg.selectAll('g.task-node').data<any>(this.taskGroup.tasks, (d) => d.taskID);

        this.node.join(
            (enter) => {
                const en = this.appendEnteringNodeElements(enter);
                this._cdRef.detectChanges();
                return this.updateChevrons(en);
            },
            (update) => {
                this.updateChevrons(update);

                if (!(_.last(this.taskGroup.tasks) as any).pointsTo.length) {
                    this.updateCompleteChevron();
                }

                return update;
            },
            (exit) => exit.transition().duration(1000).attr('fill-opacity', 1e-6).attr('stroke-opacity', 1e-6).remove(),
        );

        this._cdRef.detectChanges();
    }

    appendEnteringNodeElements(enter) {
        this.enteringNode = enter.append('g').attr('class', 'task-node');

        this.enteringNode
            .append('path')
            .attr('class', 'task-chevron')
            .attr('stroke-width', 1)
            .style('filter', 'url(#drop-shadow)');

        this.enteringNode
            .append('text')
            .attr('class', 'task-name chevron-text')
            .attr('dy', '0.35em')
            .attr('x', this.chevronWidth / 2)
            .attr('text-anchor', 'middle');

        this.enteringNode
            .append('text')
            .attr('class', 'task-user chevron-text')
            .attr('dy', '0.35em')
            .attr('x', this.chevronWidth / 2)
            .attr('text-anchor', 'middle');

        this.enteringNode
            .append('text')
            .attr('class', 'task-team chevron-text')
            .attr('dy', '0.35em')
            .attr('x', this.chevronWidth / 2)
            .attr('text-anchor', 'middle');

        this.enteringNode
            .append('text')
            .attr('class', 'task-deliverable chevron-text')
            .attr('dy', '0.35em')
            .attr('x', this.chevronWidth / 2)
            .attr('text-anchor', 'middle')
            .style('font-size', '13');

        this.enteringNode
            .append('text')
            .attr('class', 'task-checkbox')
            .attr('x', 5)
            .attr('y', 20)
            .attr('dy', '0.35em')
            .style('font', 'var(--fa-font-regular)')
            .style('font-weight', '900')
            .style('font-size', '22')
            .attr('tooltip-append-to-body', 'true')
            .text('\uf0c8');

        this.enteringNode
            .append('text')
            .attr('class', 'task-checked')
            .attr('x', 5)
            .attr('y', 20)
            .attr('dy', '0.35em')
            .style('font', 'var(--fa-font-regular)')
            .style('font-weight', '900')
            .style('font-size', '24')
            .text('\uf046');

        this.enteringNode
            .append('text')
            .attr('class', 'task-skipped')
            .attr('x', 5)
            .attr('y', 20)
            .attr('dy', '0.35em')
            .style('font', 'var(--fa-font-regular)')
            .style('font-weight', '900')
            .style('font-size', '24')
            .text('\uf147');

        return this.enteringNode;
    }

    updateChevrons(update): void {
        update
            .attr('fill-opacity', (d) => {
                return d.temp ? 1e-6 : 1;
            })
            .attr('stroke-opacity', (d) => {
                return d.temp ? 1e-6 : 1;
            })
            .transition()
            .attr('fill-opacity', 1)
            .attr('stroke-opacity', 1)
            .attr('transform', (d, i: number) => {
                const index =
                    this.taskGroup && Array.isArray(this.taskGroup.tasks)
                        ? Math.max(
                              0,
                              this.taskGroup.tasks.findIndex((t) => t.taskID === d.taskID),
                          )
                        : i;

                const y = this.padding + this._getYOffsetForIndex(index);
                return `translate(${this.padding},${y})`;
            });

        this._cdRef.detectChanges();
        this.updateChevronText(update);

        const chevron = update.select('path.task-chevron');

        chevron
            .transition()
            .attr('d', (d) => {
                const specificChevronHeight = this.getHeight(d);

                return d3.line()([
                    [0, 0],
                    [this.chevronWidth / 2, this.chevronDivot],
                    [this.chevronWidth, 0],
                    [this.chevronWidth, specificChevronHeight - this.chevronDivot],
                    [this.chevronWidth / 2, specificChevronHeight],
                    [0, specificChevronHeight - this.chevronDivot],
                    [0, 0],
                ]);
            })
            .attr('stroke', (d) => {
                return d.completedDateTime ? '#5cb85c' : 'gray';
            })
            .attr('fill', (d) => {
                return d.isReady ? 'lightblue' : '#eee';
            });

        chevron
            .on('mouseenter', (event: MouseEvent, d) => {
                if (this.contextMenu?.length) {
                    return false;
                }
                this.hoveredTask = d;
                this._cdRef.detectChanges();
                this.getPopoverPosition(event.currentTarget);
                return false;
            })
            .on('mouseleave', (event, d) => {
                this.hoveredTask = null;
                return false;
            })
            .on('contextmenu', async (event, d) => {
                event.preventDefault();
                event.stopPropagation();

                const index =
                    this.taskGroup && Array.isArray(this.taskGroup.tasks)
                        ? this.taskGroup.tasks.findIndex((t) => t.taskID === d.taskID)
                        : -1;

                const targetPath = event.currentTarget;
                this.hoveredTask = null;
                this.popoverPosition = { x: 0, y: 0 };
                this.contextMenu = [];
                this._cdRef.detectChanges();

                try {
                    const items = await this.getContextOptions(d.taskID, index);
                    this.contextMenu = items || [];
                    if (!this.contextMenu.length) {
                        this._cdRef.detectChanges();
                        return false;
                    }

                    this._cdRef.detectChanges();

                    const groupEl = (targetPath.closest('g.task-node') as Element) || targetPath;
                    const rect = groupEl.getBoundingClientRect();

                    const mousePageX =
                        event && (event as MouseEvent).clientX
                            ? (event as MouseEvent).clientX + window.scrollX
                            : rect.left + window.scrollX + rect.width / 2;
                    const mousePageY =
                        event && (event as MouseEvent).clientY
                            ? (event as MouseEvent).clientY + window.scrollY
                            : rect.bottom + window.scrollY;

                    const popoverEl = (this._elementRef.nativeElement as HTMLElement).querySelector(
                        '.task-popover.context-menu',
                    ) as HTMLElement | null;
                    const popRect = popoverEl ? popoverEl.getBoundingClientRect() : null;
                    const popW = popRect ? popRect.width : 300; // fallback width
                    const popH = popRect ? popRect.height : 200; // fallback height

                    const minX = window.scrollX + 8;
                    const maxX = window.scrollX + window.innerWidth - popW - 8;
                    const x = Math.max(minX, Math.min(mousePageX - popW / 2, maxX));

                    const minY = window.scrollY + 8;
                    const maxY = window.scrollY + window.innerHeight - popH - 8;
                    const y = Math.max(minY, Math.min(mousePageY + 8, maxY));

                    this.popoverPosition = { x, y };

                    this._cdRef.detectChanges();
                } catch {
                    this.contextMenu = null;
                    this._cdRef.detectChanges();
                }
                return false;
            });
    }

    getPopoverPosition(targetPath: any): void {
        const groupEl = (targetPath.closest('g.task-node') as Element) || targetPath;
        const rect = groupEl.getBoundingClientRect();

        const chevronCenterY = rect.top + window.scrollY + rect.height / 2;
        const chevronLeft = rect.left + window.scrollX;
        const chevronRight = rect.right + window.scrollX;

        const popoverEl = document.querySelector('.task-popover') as HTMLElement | null;
        const popRect = popoverEl ? popoverEl.getBoundingClientRect() : null;
        const popW = popRect ? popRect.width : 300; // fallback width
        const popH = popRect ? popRect.height : 200; // fallback height

        const offsetX = 8; // gap between chevron and popover
        const side = this.popoverLeftOrRight || 'left';

        let x: number;
        if (side === 'right') {
            x = chevronRight + offsetX;
        } else {
            x = chevronLeft - offsetX - popW;
        }

        const minX = window.scrollX + 8;
        const maxX = window.scrollX + window.innerWidth - popW - 8;
        x = Math.max(minX, Math.min(x, maxX));

        let y = chevronCenterY - popH / 2;
        const minY = window.scrollY + 8;
        const maxY = window.scrollY + window.innerHeight - popH - 8;
        y = Math.max(minY, Math.min(y, maxY));

        this.popoverPosition = { x, y };
    }

    async launchUserTeamPicker(task, currentIndex): Promise<void> {
        this.editingTask = structuredClone(task);

        //determine instance scope filter to use on the user picker if needed for a consultant
        const instanceId = await lastValueFrom(
            this._instanceRepository.getEntityInstanceId(this.editingTask.entityTypeID, this.editingTask.entityId),
        );
        if (this._userInstanceService.isInstanceImplicit(instanceId)) {
            this.instanceIdUserFilter = this._userInstanceService.getSelectedInstance().instanceId;
        } else {
            this.instanceIdUserFilter = null;
        }

        const taskNodes = this.svg.selectAll('g.task-node');
        const taskNodeElements = taskNodes.nodes();
        this.currentNode = d3.select(taskNodeElements[currentIndex]);
        const extraHeight = 125 - this.getHeight(task);

        this.svg.transition().attr('height', Number(this.svg.attr('height')) + extraHeight);

        this.currentNode
            .select('path.task-chevron')
            .transition()
            .attr('d', (d) => {
                const specificChevronHeight = this.getHeight(d) + extraHeight;

                return d3.line()([
                    [0, 0],
                    [this.chevronWidth / 2, this.chevronDivot],
                    [this.chevronWidth, 0],
                    [this.chevronWidth, specificChevronHeight - this.chevronDivot],
                    [this.chevronWidth / 2, specificChevronHeight],
                    [0, specificChevronHeight - this.chevronDivot],
                    [0, 0],
                ]);
            });

        this.currentNode.select('text.task-user').attr('visibility', 'hidden');

        this.currentNode.select('text.task-team').attr('visibility', 'hidden');

        this.currentNode.select('text.task-deliverable').attr('visibility', 'hidden');

        taskNodes.transition().attr('transform', (d, i, transitionNodes) => {
            if (i > currentIndex) {
                const y = _.words(d3.select(transitionNodes[i]).attr('transform'))[2];

                return `translate(${this.padding},${Number(y) + extraHeight})`;
            } else {
                return d3.select(transitionNodes[i]).attr('transform');
            }
        });

        const y = _.words(this.completeNode.attr('transform'))[2];

        this.completeNode.transition().attr('transform', `translate(${this.padding},${Number(y) + extraHeight})`);

        const picker = this.currentNode.append('g').attr('class', 'picker');

        picker
            .append('text')
            .attr('y', 60)
            .attr('width', this.chevronWidth)
            .attr('height', 30)
            .attr('dy', '0.35em')
            .attr('x', 10)
            .text('Assigned To:');
        // .style('font-weight', 'bold')

        picker
            .append('text')
            .attr('y', 85)
            .attr('width', 30)
            .attr('height', 30)
            .attr('dy', '0.35em')
            .attr('x', 215)
            .text('\uf00d')
            .style('font', 'var(--fa-font-regular)')
            .style('font-weight', '900')
            .style('font-size', '24')
            .style('cursor', 'pointer')
            .attr('fill', 'red')
            .attr('data-qa', 'task-user-picker-cancel')
            .on('click', () => {
                this.revertCurrentNode();

                this.updateTaskGroup();
            });

        const foreignObject = picker
            .append('foreignObject')
            .attr('y', 70)
            .attr('width', 200)
            .attr('height', 30)
            .attr('dy', '0.35em')
            .attr('x', 10);

        this._cdRef.detectChanges();

        const foNode = foreignObject.node() as SVGForeignObjectElement | null;
        if (foNode) {
            this.attachUserTeamPicker(foNode);
        } else {
            const foreignObjectNodes = foreignObject.nodes();
            if (foreignObjectNodes && foreignObjectNodes.length) {
                this.attachUserTeamPicker(foreignObjectNodes[0] as SVGForeignObjectElement);
            }
        }

        this._cdRef.detectChanges();
    }

    revertCurrentNode(): void {
        this.editingTask = null;

        // destroy any dynamic picker first
        this.destroyPicker();

        this.currentNode.select('g.picker').remove();

        this.currentNode.select('text.task-user').attr('visibility', 'visible');

        this.currentNode.select('text.task-team').attr('visibility', 'visible');

        this.currentNode.select('text.task-deliverable').attr('visibility', 'visible');
    }

    userPicked(): Promise<void> {
        this.waitingOnServer = true;

        // destroy picker before sending so UI is cleaned up
        this.destroyPicker();

        return this.trackTaskOperation(async () => {
            const data = await this._taskService.updateTask(this.editingTask);
            this.revertCurrentNode();
            this.updateAllTasks(data);
        });
    }

    updateChevronText(update): void {
        update
            .select('text.task-name')
            .style('font-weight', (d) => {
                return d.isReady ? 'bold' : 'normal';
            })
            .text((d) => {
                return d.name;
            })
            .each(this.wrap)
            .attr('y', this.padding + 20 + this.verticalOffset / 2)
            .attr('data-qa', 'task-name');

        update
            .select('text.task-user')
            .text((d) => {
                if (d.isReady) {
                    return `Assign: ${d.assignedUser.firstName} ${d.assignedUser.lastName}`;
                }
            })
            .each(this.wrap)
            .attr('y', this.padding + 40 + this.verticalOffset / 2)
            .attr('fill', (d) => {
                return d.assignedUserIsOverridden ? 'red' : 'initial';
            })
            .attr('data-qa', 'task-user');

        update
            .select('text.task-team')
            .text((d) => {
                if (d.isReady) {
                    return d.assignedTeam.name;
                }
            })
            .each(this.wrap)
            .attr('y', this.padding + 60 + this.verticalOffset / 2)
            .attr('fill', (d) => {
                return d.assignedTeamIsOverridden ? 'red' : 'initial';
            })
            .attr('data-qa', 'task-team');

        update
            .select('text.task-deliverable')
            .attr('y', (d) => {
                return d.isReady
                    ? this.padding + 80 + this.verticalOffset / 2
                    : this.padding + 40 + this.verticalOffset / 2;
            })
            .attr('data-qa', (d, i) => `task-deliverable-${i}`)
            .text((d) => {
                if (this.isDeliverable(d)) {
                    let deliverableStr = '';
                    let dateFormatStr = '';

                    switch (d.taskTypeID) {
                        case TaskTypes.INFORMALHEARING:
                        case TaskTypes.FORMALHEARING:
                        case TaskTypes.SUBMITEVIDENCE:
                            dateFormatStr = 'M/D/YYYY h:mm a';
                            break;
                        default:
                            dateFormatStr = 'M/D/YYYY';
                    }

                    deliverableStr += d.deliverableName ? d.deliverableName : 'Due';
                    deliverableStr += ' ';
                    deliverableStr += d.dueDate ? moment.utc(d.dueDate).format(dateFormatStr) : '?';

                    return deliverableStr;
                }
            })
            .each(this.wrap);

        update
            .select('text.task-checkbox')
            .attr('visibility', (d) => {
                return d.isReady && d.taskAuthorizationInfo.completeTask ? 'visible' : 'hidden';
            })
            .style('cursor', (d) => {
                return d.taskType.systemCompletes ? 'not-allowed' : 'pointer';
            })
            .style('fill', (d) => {
                return d.taskType.systemCompletes ? 'lightgray' : 'white';
            })
            .attr('uib-tooltip', (d) => {
                return d.taskType.systemCompletes ? 'Cannot Complete. Skip instead.' : '';
            })
            .attr('data-qa', 'task-complete-checkbox')
            .on('click', (event, d) => {
                if (d.taskType.systemCompletes) {
                    return;
                }

                this.completeTask(d, event.currentTarget);
            });

        update.select('text.task-checked').attr('visibility', (d) => {
            return d.completedDateTime && !d.skipped ? 'visible' : 'hidden';
        });

        update.select('text.task-skipped').attr('visibility', (d) => {
            return d.skipped ? 'visible' : 'hidden';
        });
    }

    wrap = (d, i: number, nodes): void => {
        // Select the actual text node D3 passed in
        const textSel = d3.select(nodes[i]);
        let text = textSel.text() || '';
        // Use the node's getComputedTextLength correctly
        let textLength = (textSel.node() as SVGTextElement).getComputedTextLength();

        // Safe class check
        const classAttr = textSel.attr('class') || '';
        const paddingMultiplier = _.includes(classAttr, 'task-name') ? 5 : 2;

        // Truncate until it fits
        while (textLength > this.chevronWidth - paddingMultiplier * this.padding && text.length > 0) {
            text = text.slice(0, -1);
            textSel.text(`${text}...`);
            textLength = (textSel.node() as SVGTextElement).getComputedTextLength();
        }
    };

    hideContextMenus(): void {
        this.contextMenu = null;
        this._cdRef.detectChanges();
    }

    updateCompleteChevron(): void {
        const lastTaskCompletedDateTime = (_.last(this.taskGroup.tasks) as any).completedDateTime;
        const strokeColor = lastTaskCompletedDateTime ? '#5cb85c' : 'gray';

        this.completeNode
            .transition()
            .attr('fill-opacity', 1)
            .attr('stroke-opacity', 1)
            .attr(
                'transform',
                `translate(${this.padding},${this._getYOffsetForIndex(this.taskGroup.tasks.length + 1) + this.padding})`,
            );

        this.completeChevron.attr('stroke', strokeColor);

        if (lastTaskCompletedDateTime) {
            this.completeCheck.text('\uf00c');
            this.completeText.attr('y', this.completeChevronHeight * 0.42);
            this.completeDate.text(moment.utc(lastTaskCompletedDateTime).tz('America/Chicago').format('M/D/YYYY'));
            this.completeNode.style('font-weight', 'bold');
        } else {
            this.completeCheck.text('');
            this.completeText.attr('y', this.completeChevronHeight / 2);
            this.completeDate.text('');
            this.completeNode.style('font-weight', 'normal');
        }
    }

    async getContextOptions(taskID: number, index: number): Promise<ContextMenuItem[]> {
        const rightClickMenuItems: ContextMenuItem[] = [];
        let task: any = {};

        // No menu if we are in edit mode
        if (this.editingTask) {
            return [];
        }

        // Load the task by taskID - so we have all variables needed
        task = _.find(this.taskGroup.tasks, (t) => {
            return t.taskID === taskID;
        });

        if (task.taskAuthorizationInfo.deleteTask && !task.taskType.systemDeletes === true) {
            const deleteTaskMenu = {
                label: 'Delete Task',
                action: () => {
                    this.deleteTask(task);
                    this.hideContextMenus();
                },
            };
            rightClickMenuItems.push(deleteTaskMenu);
        }

        if (task.taskAuthorizationInfo.reassignTask && !task.taskType.systemReassigns === true) {
            const reassignTaskMenu = {
                label: 'Reassign Task',
                action: () => {
                    this.reassignTask(task, index);
                    this.hideContextMenus();
                },
            };
            rightClickMenuItems.push(reassignTaskMenu);
        }

        if (task.taskAuthorizationInfo.rescindTask && !task.taskType.systemRescinds === true) {
            const rescindTaskMenu = {
                label: 'Rescind Task',
                action: () => {
                    this.rescindTask(task);
                    this.hideContextMenus();
                },
            };
            rightClickMenuItems.push(rescindTaskMenu);
        }

        if (task.taskAuthorizationInfo.skipTask && !task.taskType.systemSkips === true) {
            const skipTaskMenu = {
                label: 'Skip Task',
                action: () => {
                    this.skipTask(task);
                    this.hideContextMenus();
                },
            };
            rightClickMenuItems.push(skipTaskMenu);
        }

        const data = await this._taskService.getInsertOptions(task.entityId, task.entityTypeID, task.taskID);

        if (data.insertAfter.length && task.taskAuthorizationInfo.insertTaskAfter) {
            data.insertAfter = _.map(data.insertAfter, (item: any) => {
                return {
                    label: item.name,
                    action: () => {
                        this.insertTaskAfter(task, item.taskTypeID);
                        this.hideContextMenus();
                    },
                };
            });
            rightClickMenuItems.unshift({ label: 'Insert Task Below', children: data.insertAfter });
        }

        if (data.insertBefore.length && task.taskAuthorizationInfo.insertTaskBefore) {
            data.insertBefore = _.map(data.insertBefore, (item: any) => {
                return {
                    label: item.name,
                    action: () => {
                        this.insertTaskBefore(task, item.taskTypeID);
                        this.hideContextMenus();
                    },
                };
            });
            rightClickMenuItems.unshift({ label: 'Insert Task Above', children: data.insertBefore });
        }

        return rightClickMenuItems;
    }

    //Context Menu Functions
    insertTaskBefore(task, newTaskTypeID: number): Promise<void> {
        this.waitingOnServer = true;
        return this.trackTaskOperation(async () => {
            const data = await this._taskService.insertTask(
                task,
                newTaskTypeID,
                TaskActionTypes.INSERTNEWBEFORETHISTASK,
            );
            this.updateAllTasks(data);
            this.entityTaskUpdated.emit();
        });
    }

    insertTaskAfter(task, newTaskTypeID: number): Promise<void> {
        this.waitingOnServer = true;
        return this.trackTaskOperation(async () => {
            const data = await this._taskService.insertTask(
                task,
                newTaskTypeID,
                TaskActionTypes.INSERTNEWAFTERTHISTASK,
            );
            this.updateAllTasks(data);
            this.entityTaskUpdated.emit();
        });
    }

    async deleteTask(task): Promise<void> {
        if (confirm(`Are you sure you wish to delete ${task.name}?`)) {
            this.waitingOnServer = true;
            return this.trackTaskOperation(async () => {
                const response = await this._taskService.deleteTask(task);
                this.updateAllTasks(response);
                this.entityTaskUpdated.emit();
            });
        }
    }

    reassignTask(task, index: number): void {
        this.launchUserTeamPicker(task, index);

        //Added this in - if a reassign happens then we need to reload the activity panel
        this.entityTaskUpdated.emit();
    }

    async rescindTask(task): Promise<void> {
        if (confirm(`Are you sure you wish to rescind the status of ${task.name}?`)) {
            this.waitingOnServer = true;
            return this.trackTaskOperation(async () => {
                const response = await this._taskService.rescindTask(task);
                this.updateAllTasks(response);
                this.entityTaskUpdated.emit();
            });
        }
    }

    skipTask(task): Promise<void> {
        this.waitingOnServer = true;
        return this.trackTaskOperation(async () => {
            const response = await this._taskService.skipTask(task);
            this.updateAllTasks(response);
            this.entityTaskUpdated.emit();
        });
    }

    completeTask(task, clickedNode): Promise<void> {
        this.startSpinner(clickedNode, task);
        this.waitingOnServer = true;

        return this.trackTaskOperation(async () => {
            try {
                const response = await this._taskService.completeTask(task, this.allowAddYearPrompt);
                this.updateAllTasks(response);
                this.entityTaskUpdated.emit();

                if (response.addYearPrompt) {
                    this.addYearPromptRequested.emit(response.addYearPrompt);
                }
            } finally {
                this.stopSpinner(clickedNode);
                this.waitingOnServer = false;
            }
        });
    }

    getHeight(task): number {
        return (
            this.verticalOffset +
            (() => {
                if (task.isReady) {
                    return this.isDeliverable(task) ? this.heights.xl : this.heights.lg;
                } else if (this.isDeliverable(task)) {
                    return this.heights.md;
                } else {
                    return this.heights.sm;
                }
            })()
        );
    }

    updateAllTasks(data): void {
        this.updateData.emit(this._taskService.splitIntoGroups(data.tasks));
        this.waitingOnServer = false;
        this.stopSpinner();
    }

    isDeliverable(task): boolean {
        return !!(task.deliverableName || task.dueDate);
    }

    startSpinner(clickedNode, task): void {
        d3.select(clickedNode).attr('visibility', 'hidden');

        d3.select(clickedNode.parentNode)
            .append('g')
            .attr('class', 'spinner-group')
            .style('transform', 'translate(16px, 20px)')
            .append('text')
            .attr('class', 'fa fa-spinner fa-pulse')
            .text('\uf110')
            .attr('x', -12)
            .attr('y', 9)
            .style('font', 'var(--fa-font-regular)')
            .style('font-weight', '900')
            .style('font-size', '24');
    }

    stopSpinner(clickedNode?): void {
        if (clickedNode) {
            d3.select(clickedNode).attr('visibility', '');
        }
        d3.select('g.spinner-group').remove();
    }

    private trackTaskOperation(operation: () => Promise<void>): Promise<void> {
        const promise = operation();
        this.taskOperationStarted.emit(promise);
        return promise;
    }

    private attachUserTeamPicker(foreignObjectNode: SVGForeignObjectElement): void {
        // cleanup any existing picker first
        this.destroyPicker();

        const hostDiv = document.createElement('div');
        hostDiv.style.width = '100%';
        hostDiv.style.height = '100%';
        hostDiv.style.display = 'block';
        hostDiv.style.padding = '0';
        hostDiv.style.margin = '0';

        foreignObjectNode.appendChild(hostDiv);
        this._pickerHostDiv = hostDiv;

        const compRef = this._vcr.createComponent<UserTeamPickerComponent>(UserTeamPickerComponent, {
            injector: this._injector,
        });

        compRef.setInput('user', this.editingTask.assignedUser);
        compRef.setInput('userId', this.editingTask.assignedUserID);
        compRef.setInput('teamId', this.editingTask.assignedTeamID);
        compRef.setInput('isReassign', true);
        compRef.setInput('entityIdScope', this.editingTask.taskID);
        compRef.setInput('entityTypeScope', 'task');
        compRef.setInput('instanceIdScope', this.instanceIdUserFilter);
        compRef.setInput('revertIfEmptyOnBlur', true);

        const hostEl = (compRef.location && (compRef.location.nativeElement as Node)) || null;
        if (hostEl) {
            hostDiv.appendChild(hostEl);
        } else {
            // fallback: use rootNodes if available
            const rootNodes = (compRef.hostView as any).rootNodes as Node[];
            if (rootNodes && rootNodes.length) {
                rootNodes.forEach((n) => hostDiv.appendChild(n));
            }
        }

        if (compRef.instance.userChange && compRef.instance.userChange.subscribe) {
            const sub = compRef.instance.userChange.subscribe((user) => {
                // update editingTask with selected user/team
                this.editingTask.assignedUser = user;
                this.editingTask.assignedUserID = user?.userID;
                this.editingTask.assignedTeam = user ? { teamID: user.teamID, name: user.teamName } : null;
                this.editingTask.assignedTeamID = user?.teamID;

                // commit the change
                this.userPicked();
            });
            this._pickerSubscriptions.push(sub);
        }

        if (compRef.instance.userIdChange && compRef.instance.userIdChange.subscribe) {
            const sub2 = compRef.instance.userIdChange.subscribe((id) => {
                this.editingTask.assignedUserID = id;
            });
            this._pickerSubscriptions.push(sub2);
        }

        if (compRef.instance.teamIdChange && compRef.instance.teamIdChange.subscribe) {
            const sub3 = compRef.instance.teamIdChange.subscribe((teamId) => {
                this.editingTask.assignedTeamID = teamId;
            });
            this._pickerSubscriptions.push(sub3);
        }

        compRef.changeDetectorRef.detectChanges();

        this._pickerCompRef = compRef;
    }

    private _getYOffsetForIndex(index: number): number {
        if (!this.taskGroup || !Array.isArray(this.taskGroup.tasks) || index <= 0) {
            return 0;
        }

        let y = 0;
        const maxIndex = Math.min(index, this.taskGroup.tasks.length);
        for (let k = 0; k < maxIndex; k++) {
            y += this.getHeight(this.taskGroup.tasks[k]);
        }
        return y;
    }

    private destroyPicker(): void {
        // unsubscribe
        this._pickerSubscriptions.forEach((s) => {
            try {
                s.unsubscribe();
            } catch (err) {
                // ignore
            }
        });
        this._pickerSubscriptions = [];

        // destroy the component
        if (this._pickerCompRef) {
            try {
                this._pickerCompRef.destroy();
            } catch (err) {
                // ignore
            }
            this._pickerCompRef = null;
        }

        // remove host div
        if (this._pickerHostDiv && this._pickerHostDiv.parentNode) {
            try {
                this._pickerHostDiv.parentNode.removeChild(this._pickerHostDiv);
            } catch (err) {
                // ignore
            }
            this._pickerHostDiv = null;
        }
    }
}
